import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { FormArray, FormControl, FormGroup, Validators } from "@angular/forms";
import { takeUntil } from "rxjs/operators";
import { SidenavService } from "src/app/layout/sidenav/sidenav.service";
import { ApiService } from "src/app/services/api.service";
import endpoints from "src/app/helpers/endpoints";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs/internal/Subject";
import { fadeInRightAnimation } from "src/@fury/animations/fade-in-right.animation";
import { fadeInUpAnimation } from "src/@fury/animations/fade-in-up.animation";
import { scaleInAnimation } from "src/@fury/animations/scale-in.animation";
import { MapsService } from "src/app/services/maps.service";
import { ErrorType } from "src/app/enums/error-type.enum";
import { AddressInterface, CommonService } from "src/app/services/common.service";

@Component({
  selector: "fury-edit-customer",
  templateUrl: "./edit-customer.component.html",
  styleUrls: ["./edit-customer.component.scss"],
  animations: [fadeInUpAnimation, fadeInRightAnimation, scaleInAnimation],
})
export class EditCustomerComponent implements OnInit {
  customerForm: FormGroup;
  coveredAddresses = [];
  userId = "";
  isFocused: boolean = false;
  fetchUser = false;
  currentYear: number = Number(new Date().getFullYear().toString().slice(2, 4));
  currentMonth = new Date().getMonth();
  ngUnsubscribe = new Subject<void>();
  @ViewChild("billingLocation", { static: false })
  public billingLocation!: ElementRef;
  @ViewChild("malingLocation", { static: false })
  public malingLocation!: ElementRef;

  personalInformationError = 'Invalid Personal Information';
  billingAddressError = 'Invalid Billing Address';
  mailingAddressError = 'Invalid Mailing Address';
  cardError = 'Invalid Card Information';

  customerFieldErroes = {
    firstName: this.personalInformationError,
    lastName: this.personalInformationError,
    emailAddress: this.personalInformationError,
    phoneNumber: this.personalInformationError,
    secondaryPhoneNumber: this.personalInformationError,
    billingStreetAddress: this.billingAddressError,
    billingUnitNumber: this.billingAddressError,
    billingCity: this.billingAddressError,
    billingState: this.billingAddressError,
    billingZip: this.billingAddressError,
    mailingStreetAddress: this.mailingAddressError,
    mailingUnitNumber: this.mailingAddressError,
    mailingCity: this.mailingAddressError,
    mailingState: this.mailingAddressError,
    mailingZip: this.mailingAddressError,
    cardDetail: this.cardError,
    cardHolderName: this.cardError,
    cardNumber: this.cardError,
    cardExpirationDate: this.cardError,
    isDefault: this.cardError,
    customerScore: this.personalInformationError,
    agreementDefault: this.cardError,
  }
  customerScores = ["A+","A","A-","B+","B","B-","C+","C","C-","D","E","F"];

  constructor(
    private sidenavService: SidenavService,
    public apiService: ApiService,
    private activateRoute: ActivatedRoute,
    private router: Router,
    private mapsService: MapsService,
    private commonService: CommonService
  ) { }

  ngOnInit(): void {
    this.setupCustomerForm();
    this.userId = this.activateRoute.snapshot.params["id"];
    this.getUserDetails()
    setTimeout(() => {
      this.sidenavService.close();
    }, 0)
  }

  setupCustomerForm() {
    this.customerForm = new FormGroup({
      firstName: new FormControl(null, [
        Validators.maxLength(50),
        Validators.minLength(1),
        Validators.required,
      ]),

      lastName: new FormControl(null, [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(50),
      ]),
      emailAddress: new FormControl(null, [
        Validators.required,
        Validators.pattern(/^[\w.-]+@[\w.-]+\.\w{2,3}$/),
      ]),
      phoneNumber: new FormControl(null, [
        Validators.required,
        Validators.pattern(/^[2-9][0-8]\d{1}[2-9]\d{2}\d{4}$/),
      ]),
      secondaryPhoneNumber: new FormControl(null, [

        Validators.pattern(/^[2-9][0-8]\d{1}[2-9]\d{2}\d{4}$/),
      ]),
      billingStreetAddress: new FormControl(null, [Validators.required]),
      billingUnitNumber: new FormControl(null),
      billingCity: new FormControl(null, [Validators.required]),
      billingState: new FormControl(null, [Validators.required]),
      billingZip: new FormControl(null, [Validators.required]),
      mailingStreetAddress: new FormControl(null, [Validators.required]),
      mailingUnitNumber: new FormControl(null),
      mailingCity: new FormControl(null, [Validators.required]),
      mailingState: new FormControl(null, [Validators.required]),
      mailingZip: new FormControl(null, [Validators.required]),
      cardDetail: new FormArray([]),
      customerScore: new FormControl(null),
    });
  }

  getUserDetails() {
    this.coveredAddresses = [];
    this.apiService
      .get(endpoints.customerDashboard.getUser(this.userId))
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: any) => {
        const user = res.body.user;
        const coveredAddresses = res?.body?.coveredAddresses || [];
        coveredAddresses.forEach((ele: AddressInterface) => this.coveredAddresses.push(this.commonService.formatAddress(ele)))

        this.customerForm.setValue({
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          emailAddress: user?.emailAddress || '',
          phoneNumber: user?.phoneNumber || '',
          secondaryPhoneNumber: user?.secondaryPhoneNumber || '',
          billingStreetAddress: user?.billingAddress?.streetAddress || '',
          billingUnitNumber: user?.billingAddress?.unitNumber || '',
          billingCity: user?.billingAddress?.city || '',
          billingState: user?.billingAddress?.state || '',
          billingZip: user?.billingAddress?.zip || '',
          mailingStreetAddress: user?.mailingAddress?.streetAddress || '',
          mailingUnitNumber: user?.mailingAddress?.unitNumber || '',
          mailingCity: user?.mailingAddress?.city || '',
          mailingState: user?.mailingAddress?.state || '',
          mailingZip: user?.mailingAddress?.zip || '',
          cardDetail: [],
          customerScore: user?.customerScore || ''
        })
        const cardDetailFormArray = this.customerForm.controls
          .cardDetail as FormArray;

        this.customerForm.updateValueAndValidity();
        user.userPaymentDetail?.forEach(ele => {
          const card = ele.cardDetails;          ;
          const formattedMonth = card.expiryMonth?.length === 1 ? `0${card.expiryMonth}` : card.expiryMonth;
          const shortYear = card?.expiryYear?.slice(2);

          const newCard = new FormGroup({
            cardHolderName: new FormControl(card?.cardHolderName || '', [
              Validators.required,
              Validators.minLength(2),
            ]),
            cardNumber: new FormControl(card?.cardNumber || '', [Validators.required]),
            cardExpirationDate: new FormControl(`${formattedMonth}/${shortYear}` || '', [Validators.required]),
            isDefault: new FormControl(ele?.isDefault || false),
            cardType: new FormControl(card?.cardType || ''),
            cvv: new FormControl(card?.cvv || '', [Validators.required]),
            agreementDefault: new FormControl(ele?.agreementDefault || false),
          });

          cardDetailFormArray.push(newCard);

        })

      });
  }

  findInvalidControls() {
    const invalid = [];
    const controls = this.customerForm.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(this.customerFieldErroes[name]);
      }
    }
    return [...new Set(invalid)].join(', ');
  }

  updateCustomer() {
    this.fetchUser = false;
    if (this.customerForm.invalid) {
      this.customerForm.markAllAsTouched();
      this.apiService.alert(this.findInvalidControls(), ErrorType.Error);
      return;
    }
    const payload = this.createPayload();
    this.apiService
      .post(endpoints.customerDashboard.updateUser(this.userId), payload)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: any) => {
        this.fetchUser = true;
        this.apiService.alert(res.body.message, ErrorType.Success);
        this.redirectToDashboard();
      }, (err) => {
        this.apiService.alert(err.message, ErrorType.Error);
      });
  }

  createPayload() {
    const cardDetails = JSON.parse(JSON.stringify(this.customerForm.value.cardDetail));
    if (cardDetails.length > 0) {
      cardDetails.forEach((ele, index) => {
        const exipreDate = ele.cardExpirationDate;

        const [month, year] = exipreDate.includes("/") ? exipreDate.split("/") : [exipreDate.substring(0, 2), exipreDate.substring(2, 4)]
        if (year?.length <= 2) {
          cardDetails[index].cardExpirationDate = `${month}/20${year}`;
        }
      });
    }
    return {
      firstName: this.customerForm.value.firstName,
      lastName: this.customerForm.value.lastName,
      emailAddress: this.customerForm.value.emailAddress,
      phoneCountryCode: "",
      phoneNumber: this.customerForm.value.phoneNumber,
      secondaryPhoneNumber: this.customerForm.value.secondaryPhoneNumber,
      billingAddress: {
        streetAddress: this.customerForm.value.billingStreetAddress.split(',')[0],
        unitNumber: this.customerForm.value.billingUnitNumber,
        city: this.customerForm.value.billingCity,
        state: this.customerForm.value.billingState,
        zip: this.customerForm.value.billingZip,
      },
      mailingAddress: {
        streetAddress: this.customerForm.value.mailingStreetAddress.split(',')[0],
        unitNumber: this.customerForm.value.mailingUnitNumber,
        city: this.customerForm.value.mailingCity,
        state: this.customerForm.value.mailingState,
        zip: this.customerForm.value.mailingZip,
      },
      cardDetail: cardDetails,
      customerScore: this.customerForm.value.customerScore
    };

  }

  addNewCard(event) {
    event.preventDefault();
    const cardDetailFormArray = this.customerForm.controls
      .cardDetail as FormArray;
    const newCard = new FormGroup({
      cardHolderName: new FormControl("", [
        Validators.required,
        Validators.minLength(2),
      ]),
      cardNumber: new FormControl("", [Validators.required]),
      cardExpirationDate: new FormControl("", [Validators.required]),
      isDefault: new FormControl(false),
      cardType: new FormControl(""),
      cvv: new FormControl("", [Validators.required]),
      agreementDefault: new FormControl(false)
    });

    cardDetailFormArray.push(newCard);
    this.customerForm.updateValueAndValidity();
  }

  handleDefaultChange(index: number) {
    const cardDetailFormArray = this.customerForm.controls
      .cardDetail as FormArray;
    if (!cardDetailFormArray?.controls?.length) return;
    cardDetailFormArray.controls.forEach((ele) => {
      ele.get("isDefault").setValue(false);
    });

    cardDetailFormArray.controls[index].get("isDefault").setValue(true);
    cardDetailFormArray.updateValueAndValidity();
  }
   handleAgreementDefaultChange(index: number) {
    const cardDetailFormArray = this.customerForm.controls
      .cardDetail as FormArray;
    if (!cardDetailFormArray?.controls?.length) return;
    cardDetailFormArray.controls.forEach((ele) => {
      ele.get("agreementDefault").setValue(false);
    });

    cardDetailFormArray.controls[index].get("agreementDefault").setValue(true);
    cardDetailFormArray.updateValueAndValidity();
  }

  removeCard(i) {
    const cardDetailFormArray = this.customerForm.controls
      .cardDetail as FormArray;
    cardDetailFormArray.removeAt(i);
    cardDetailFormArray.updateValueAndValidity();
  }

  redirectToDashboard() {
    this.router.navigateByUrl(`customer-dashboard/${this.userId}`);
  }

  detectCardType(cardNumber: string, index: number) {
    const cardForm = (this.customerForm.get("cardDetail") as FormArray).controls[index];
    const oldCard = JSON.stringify(cardForm.get("cardType").value);

    const currentCard = JSON.stringify(this.getCardType(cardNumber));

    if (oldCard === currentCard) return;

    cardForm.get("cardType").setValue(this.getCardType(cardNumber));
    cardForm.get("cvv").setValue('')
  }
  getCardType(cardNumber: string): string {
    const cardNumberDigitsOnly = cardNumber.replace(/\D/g, "");
    const firstDigit = cardNumberDigitsOnly.charAt(0);
    if (firstDigit === "4") {
      return "Visa";
    } else if (firstDigit === "5") {
      return "Mastercard";
    } else if (firstDigit === "3") {
      return "American Express";
    } else if (firstDigit === "6") {
      return "Discover";
    } else {
      return "Unknown";
    }
  }

  handleExpChange(event, index: number) {
    const exipreDate = event.target.value;
    const [month, year] = exipreDate.split("/");
    if (this.currentYear > Number(year)) {
      (this.customerForm.get("cardDetail") as FormArray).controls[index]
        .get("cardExpirationDate").setErrors({ invalidExpiry: true });
      return;
    }
    if (this.currentYear === Number(year)) {
      if (month <= this.currentMonth) {
        (this.customerForm.get("cardDetail") as FormArray).controls[index]
          .get("cardExpirationDate").setErrors({ invalidExpiry: true });
      }
    }
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  ngAfterViewInit() {
    setTimeout(() => {
      if (this.billingLocation)
        this.mapsService.autoFillLocation(
          this.billingLocation,
          "billingLocation"
        );

      if (this.malingLocation)
        this.mapsService.autoFillLocation(
          this.malingLocation,
          "malingLocation"
        );
    }, 200);

    this.mapsService.getaddressEvent
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: any) => {
        if (res && typeof res === "object" && res.root === "billingLocation") {
          this.customerForm.get("billingStreetAddress").setValue(res?.address);
          this.customerForm.get("billingCity").setValue(res?.city);
          this.customerForm.get("billingState").setValue(res?.state);
          this.customerForm.get("billingZip").setValue(res?.zipcode);
        }

        if (res && typeof res === "object" && res.root === "malingLocation") {
          this.customerForm.get("mailingStreetAddress").setValue(res?.address);
          this.customerForm.get("mailingCity").setValue(res?.city);
          this.customerForm.get("mailingState").setValue(res?.state);
          this.customerForm.get("mailingZip").setValue(res?.zipcode);
        }
      });
  }
}
